import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';

/**
 * Multi-select dropdown leve com busca por nome. Pensado para listas
 * de até ~500 itens (não virtualiza ainda — render simples scrollable).
 *
 * Props:
 *   - label: rótulo curto exibido no botão quando nada selecionado
 *   - options: [{id, nome}]
 *   - value: number[] (IDs selecionados)
 *   - onChange: (ids:number[]) => void
 *   - placeholderTodos: string mostrado quando seleção vazia (ex: "Todas")
 *   - searchPlaceholder: string
 */
const MultiSelectFilter = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholderTodos = 'Todas',
  searchPlaceholder = 'Buscar…',
}) => {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => String(o.nome || '').toLowerCase().includes(q));
  }, [options, busca]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(value.filter(v => v !== id));
    else onChange([...value, id]);
  };

  const limpar = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const resumo = value.length === 0
    ? placeholderTodos
    : value.length === 1
      ? options.find(o => o.id === value[0])?.nome || '1 selecionado'
      : `${value.length} selecionados`;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between gap-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-md px-3 py-1.5 text-sm text-white min-w-[160px]"
      >
        <span className="truncate">
          <span className="text-indigo-100 mr-1">{label}:</span>
          {resumo}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value.length > 0 && (
            <FontAwesomeIcon
              icon={faTimes}
              onClick={limpar}
              className="text-indigo-100 hover:text-white text-xs"
              title="Limpar"
            />
          )}
          <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 right-0 w-72 bg-white border border-gray-200 rounded-md shadow-lg text-gray-900">
          <div className="p-2 border-b">
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-2 top-2.5 text-gray-400 text-xs" />
              <input
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-indigo-500"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">Nenhum resultado</div>
            )}
            {filtered.map(opt => {
              const checked = selectedSet.has(opt.id);
              return (
                <div
                  key={opt.id}
                  onClick={() => toggle(opt.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 ${checked ? 'bg-indigo-50' : ''}`}
                >
                  <div className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${checked ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'}`}>
                    {checked && <FontAwesomeIcon icon={faCheck} className="text-white text-[10px]" />}
                  </div>
                  <span className="truncate">{opt.nome}</span>
                </div>
              );
            })}
          </div>
          {value.length > 0 && (
            <div className="p-2 border-t text-right">
              <button
                onClick={() => onChange([])}
                className="text-xs text-indigo-600 hover:underline"
              >
                Limpar seleção ({value.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
